import { clearDemoScheduleLocal } from "../../utils/clearDemoScheduleLocal";
import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Modal from "./Modal";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useDemo } from "../../contexts/DemoContext";

const breatheAnimation = keyframes`
  0%, 100% {
    filter: brightness(1);
    transform: translateY(0);
  }
  50% {
    filter: brightness(1.1);
    transform: translateY(-1px);
  }
`;

const DemoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const DemoItem = styled.li`
  background: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.white};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[600] : theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[600] : theme.colors.neutral[50]};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.isDarkMode ? "none" : theme.shadows.md};
  }

  &.selected {
    border: 2px solid ${({ theme }) => theme.colors.primary[500]};
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[600] : theme.colors.primary[50]};
  }
`;

const DemoInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-right: 1rem;
`;

const DemoName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.isDarkMode ? theme.colors.white : theme.colors.neutral[900]};
  margin-bottom: 0.5rem;
`;

const DemoDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.isDarkMode ? theme.colors.white : theme.colors.neutral[600]};
  opacity: ${({ theme }) => theme.isDarkMode ? 0.9 : 1};
  line-height: 1.4;
`;

const DemoStatus = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.isDarkMode ? theme.colors.white : theme.colors.neutral[900]};
  opacity: ${({ theme }) => theme.isDarkMode ? 0.7 : 0.8};
  margin-top: 0.25rem;
`;

const CompletedBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.isDarkMode ? theme.colors.white : theme.colors.neutral[900]};
  font-size: 0.95rem;
  font-weight: 500;
  flex-shrink: 0;
`;

const ResetButton = styled.button`
  background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
  color: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[100] : theme.colors.neutral[900]};
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.85rem;
  transition: background 0.2s;
  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
  }
`;

const StartButton = styled.button`
  background: ${({ theme }) => theme.isDarkMode ? theme.colors.primary[600] : theme.colors.primary[600]};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.primary[500] : theme.colors.primary[500]};
    transform: translateY(-1px);
  }

  &:disabled {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[300]};
    cursor: not-allowed;
    transform: none;
    opacity: 0.7;
  }
`;

const Title = styled.h2`
  && {
    margin-bottom: ${(p) => p.theme.spacing[4]};
    color: ${({ theme }) => theme.isDarkMode ? theme.colors.white : theme.colors.neutral[900]};
    font-size: ${(p) => p.theme.typography.fontSizes["2xl"]};
    font-weight: ${(p) => p.theme.typography.fontWeights.semibold};
    border-width: 0;
  }
`;

const Description = styled.p`
  && {
    margin-bottom: 1.5rem;
    color: ${({ theme }) => theme.isDarkMode ? theme.colors.white : theme.colors.neutral[900]};
    opacity: 0.9;
    border-width: 0;
  }
`;

const Footer = styled.div`
  && {
    margin-top: 1.5rem;
    font-size: 0.75rem;
    color: ${({ theme }) => theme.isDarkMode ? theme.colors.white : theme.colors.neutral[900]};
    text-align: center;
    border-width: 0;
    opacity: 0.7;
  }
`;

const LogoutLink = styled.button`
  font-size: 0.875rem;
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;
  color: ${({ theme }) => theme.isDarkMode ? '#00ffd0' : '#174289'};

  &:hover {
    color: ${({ theme }) => theme.isDarkMode ? '#1aff89' : '#1a56db'};
  }
`;

const DemoSelectionModal = ({
  isOpen,
  onClose,
  onResetDemo,
  onSelect,
}) => {
  const [selectedDemoId, setSelectedDemoId] = useState(null);
  const [demos] = useState([
    {
      id: "Existing Client",
      name: "Existing Client Review",
      description: "Guide through a comprehensive annual review with an existing client, showcasing our AI-powered tools for client engagement and portfolio analysis.",
      completed: false,
      recommended: true
    },
    {
      id: "New Client",
      name: "New Client Onboarding", 
      description: "Experience our streamlined onboarding process, from initial contact to comprehensive financial planning.",
      completed: false,
      preview: true
    },
    {
      id: "Portfolio Management",
      name: "Portfolio Management Suite",
      description: "Explore our advanced portfolio management tools, including performance analytics, rebalancing, and tax optimization.",
      completed: false,
      comingSoon: true
    },
    {
      id: "Financial Planning",
      name: "Financial Planning Tools",
      description: "Discover our suite of financial planning tools for retirement, education, estate planning and more.",
      completed: false,
      comingSoon: true
    },
    {
      id: "Client Portal",
      name: "Client Portal Experience",
      description: "Preview the client's view of their financial dashboard, documents, and communication tools.",
      completed: false,
      preview: true
    }
  ]);
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { completedDemos } = useDemo();

  if (!isOpen) return null;

  const handleDemoClick = (demo) => {
    if (!demo.completed && !demo.comingSoon) {
      if (demo.preview) {
        onClose();
      } else {
        setSelectedDemoId(demo.id === selectedDemoId ? null : demo.id);
      }
    }
  };

  const handleStartDemo = () => {
    if (selectedDemoId) {
      onSelect(selectedDemoId);
    }
  };

  const handleResetClick = (e, demoId) => {
    e.stopPropagation();
    if (demoId) {
      onResetDemo(demoId);
      if (demoId === selectedDemoId) {
        setSelectedDemoId(null);
      }
    }
  };

  const handleLogout = async () => {
    logout();
    // Ensure full reset and return to login
    navigate("/", { replace: true, state: { showDemoSelect: false } });
  };

  // Add completion status to demos
  const demosWithStatus = demos.map(demo => ({
    ...demo,
    completed: !!completedDemos?.[demo.id],
    lastAttempted: completedDemos?.[demo.id] ? new Date(completedDemos[demo.id]).toLocaleString() : null
  }));

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      width="large"
      closeOnOverlayClick={false}
      backgroundClassName={
        isDarkMode
          ? "bg-neutral-800 text-white"
          : "bg-gradient-to-b from-[#e9f3ff] via-white to-white text-gray-900"
      }
    >
      <div className="flex justify-between items-center mb-1">
        <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-[#174289]"}`}>Welcome to the Quanto Demo Experience!</h2>
        <LogoutLink onClick={handleLogout}>
          Logout
        </LogoutLink>
      </div>
      <p className="mb-2 opacity-90 text-[12px]">
        Select a demo experience to begin, or track your progress through completed experiences. Each demo
        showcases a range of tools and features available in the platform.
      </p>
      <div className="space-y-1.5">
        {demosWithStatus.map((demo) => (
            <div
              key={demo.id}
              onClick={() => handleDemoClick(demo)}
              className={`
                p-2 rounded-lg transition-all duration-200
                ${demo.completed 
                  ? (isDarkMode 
                      ? 'bg-neutral-700/60 border border-neutral-600' 
                      : 'bg-gray-50/60 border border-gray-200')
                  : 'bg-transparent dark:bg-neutral-700 border border-gray-200 dark:border-neutral-600'}
                ${selectedDemoId === demo.id && (isDarkMode
                  ? 'border-2 !border-transparent bg-gradient-to-r from-[#02987b] to-[#00ffd0] shadow-[0_0_15px_rgba(0,255,208,0.3)] before:absolute before:inset-[2px] before:bg-neutral-700 before:rounded-lg before:z-[-1] relative z-[1]'
                  : 'border-2 !border-transparent bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-700 shadow-md before:absolute before:inset-[2px] before:bg-white before:rounded-lg before:z-[-1] relative z-[1]')}
                ${demo.comingSoon
                  ? 'cursor-not-allowed hover:bg-gray-50/40 dark:hover:bg-neutral-700/80' 
                  : 'cursor-pointer hover:bg-white/40 dark:hover:bg-neutral-600 hover:-translate-y-0.5'}
                ${demo.completed ? 'opacity-75' : ''}
              `}
            >
              <div className="flex justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <h3 className={`text-[13px] font-medium ${
                      selectedDemoId === demo.id && !isDarkMode ? 'text-primary-700' : ''
                    }`}>{demo.name}</h3>
                    {demo.recommended && !demo.completed && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-md">
                        Recommended
                      </span>
                    )}
                    {demo.preview && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-neutral-600 text-gray-600 dark:text-gray-300 rounded-md">
                        Preview
                      </span>
                    )}
                    {demo.comingSoon && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-neutral-600 text-gray-600 dark:text-gray-300 rounded-md">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-600 dark:text-gray-300">{demo.description}</p>
                </div>
                <div className="flex flex-col items-end justify-between ml-3 min-h-[50px]">
                  {demo.completed && (
                    <>
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded flex items-center gap-1 group relative">
                        <span>Completed</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute left-full pl-1.5 whitespace-nowrap text-[10px] text-gray-500 dark:text-gray-400">
                          {demo.lastAttempted}
                        </span>
                      </span>
                      <button
                        onClick={(e) => handleResetClick(e, demo.id)}
                        title="Reset Demo"
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 bg-transparent border-0 p-0 cursor-pointer focus:outline-none mt-1.5"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                    </>
                  )}
                  {!demo.completed && demo.lastAttempted && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      Last attempted: {demo.lastAttempted}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={handleStartDemo}
          disabled={!selectedDemoId}
          className={`
            w-full mt-2 py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all duration-200
            ${!selectedDemoId 
                    ? 'bg-gray-100 dark:bg-neutral-800 border-2 border-gray-300 dark:border-neutral-600 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
      : `${isDarkMode
        ? 'bg-gradient-to-r from-[#02987b] to-[#00ffd0] hover:shadow-[#00ffd0]/20'
        : 'bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-700 hover:shadow-blue-400/20'
      } text-white shadow-sm
      hover:shadow-lg
      transform hover:-translate-y-0.5 active:translate-y-0
      animate-breathing`
            }
          `}
        >
          Start Selected Demo
        </button>
        <div className="mt-1 text-center">
          {!selectedDemoId && (
            <span className="text-[11px] text-gray-500 dark:text-gray-400">
              Select a demo experience to continue
            </span>
          )}
        </div>
        <p className="mt-0.5 text-center text-[10px] text-gray-500 dark:text-gray-400">
          Demo mode. All data is simulated. No real client data is shown.
        </p>
    </Modal>
  );
};

DemoSelectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onResetDemo: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default DemoSelectionModal;
